import axios from "axios"
import type { GrupoInfo } from "@/stores/sessionStore"

/** Cliente sem interceptor — evita deadlock no refresh (interceptor → ensureFreshToken → renovarToken → apiClient → interceptor). */
const authClient = axios.create({ timeout: 10_000, withCredentials: true })

/**
 * Fluxo de autenticação com cookie HttpOnly (padrão seguir-api):
 * o refresh token vive no cookie do backend; o access token fica em memória.
 * Este módulo substitui o keycloak-js para o fluxo de login/refresh/logout.
 */

const AUTH_API_URL = import.meta.env.VITE_AUTH_URL ?? ""
const AUTH_URL = import.meta.env.VITE_KEYCLOAK_URL ?? ""
const REALM = import.meta.env.VITE_KEYCLOAK_REALM ?? ""
const CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID ?? ""
const KEYCLOAK_LOGOUT_URI = import.meta.env.VITE_KEYCLOAK_LOGOUT_URI ?? ""

const GOVBR_AUTH_URL = import.meta.env.VITE_KEYCLOAK_URL_GOVBR || AUTH_URL
const GOVBR_REALM = import.meta.env.VITE_KEYCLOAK_REALM_GOVBR ?? ""
const GOVBR_CLIENT_ID = import.meta.env.VITE_KEYCLOAK_CLIENT_ID_GOVBR ?? ""
const GOVBR_LOGOUT_URI = import.meta.env.VITE_KEYCLOAK_LOGOUT_URI_GOVBR || ""

const POST_LOGOUT_REDIRECT_URI = import.meta.env.VITE_REDIRECT_URI || window.location.origin
const REDIRECT_URI = `${window.location.origin}/auth/callback`
const SCOPE = "openid profile email"

/** Fluxo de autenticação: realm interno (SIGI) ou realm de cidadãos (gov.br). */
export type FluxoLogin = "interno" | "govbr"

interface KeycloakConfig {
  url: string
  realm: string
  clientId: string
  redirectUri: string
  logoutUri: string
}

function configuracao(fluxo: FluxoLogin): KeycloakConfig {
  if (fluxo === "govbr") {
    return {
      url: GOVBR_AUTH_URL,
      realm: GOVBR_REALM,
      clientId: GOVBR_CLIENT_ID,
      redirectUri: REDIRECT_URI,
      logoutUri: GOVBR_LOGOUT_URI,
    }
  }
  return {
    url: AUTH_URL,
    realm: REALM,
    clientId: CLIENT_ID,
    redirectUri: REDIRECT_URI,
    logoutUri: KEYCLOAK_LOGOUT_URI,
  }
}

/** True quando o realm/client de cidadãos está configurado no build. */
export function govbrConfigurado(): boolean {
  return Boolean(GOVBR_AUTH_URL && GOVBR_REALM && GOVBR_CLIENT_ID)
}

export interface AuthSession {
  accessToken: string
  tokenType: string
  expiresIn: number
}

let accessToken: string | null = null
let accessTokenExpiresAt = 0
let fluxoAtual: FluxoLogin = "interno"
const refreshPromises = new Map<FluxoLogin, Promise<boolean>>()

export function getFluxoAtual(): FluxoLogin {
  return fluxoAtual
}

export function getAccessToken(): string | null {
  return accessToken
}

export function isAccessTokenExpired(): boolean {
  return Date.now() >= accessTokenExpiresAt
}

function setAccessToken(session: AuthSession): void {
  accessToken = session.accessToken
  accessTokenExpiresAt = Date.now() + (session.expiresIn - 30) * 1000
}

const LOGIN_RETURN_KEY = "sigi_login_return"

/**
 * Guarda o destino interno pós-login (ex.: preenchimento de formulário público
 * restrito) e leva à tela de login. Só aceita caminho interno da SPA.
 */
export function inicioLoginComRetorno(destino: string, fluxo: FluxoLogin = "interno"): void {
  if (destino.startsWith("/") && !destino.startsWith("//")) {
    sessionStorage.setItem(LOGIN_RETURN_KEY, destino)
  }
  if (fluxo === "govbr" && govbrConfigurado()) {
    void iniciarLogin("govbr")
    return
  }
  window.location.assign("/login")
}

/** Consome (uma vez) o destino pós-login; padrão = home. */
export function consumirRetornoLogin(): string {
  const destino = sessionStorage.getItem(LOGIN_RETURN_KEY)
  sessionStorage.removeItem(LOGIN_RETURN_KEY)
  return destino && destino.startsWith("/") && !destino.startsWith("//") ? destino : "/"
}

/** Monta a URL de autorização do Keycloak (exportada para testes). */
export function montarUrlAutorizacao(
  state: string,
  codeChallenge: string,
  fluxo: FluxoLogin = "interno",
): string {
  if (fluxo === "govbr" && !govbrConfigurado()) {
    throw new Error("Login gov.br não configurado neste ambiente.")
  }
  const config = configuracao(fluxo)
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: "code",
    redirect_uri: config.redirectUri,
    scope: SCOPE,
    state,
    code_challenge: codeChallenge,
    code_challenge_method: "S256",
  })
  if (fluxo === "govbr") {
    params.set("kc_idp_hint", "govbr")
  }
  return `${config.url}/realms/${config.realm}/protocol/openid-connect/auth?${params.toString()}`
}

/**
 * Inicia o login: gera PKCE e redireciona para a página do Keycloak.
 * `fluxo` escolhe o realm: `interno` (testes) ou `govbr` (homologacao).
 */
export async function iniciarLogin(fluxo: FluxoLogin = "interno"): Promise<void> {
  const codeVerifier = gerarCodeVerifier()
  const state = crypto.randomUUID()
  sessionStorage.setItem("sigi_pkce_verifier", codeVerifier)
  sessionStorage.setItem("sigi_pkce_state", state)
  sessionStorage.setItem("sigi_pkce_fluxo", fluxo)
  const challenge = await gerarCodeChallenge(codeVerifier)
  fluxoAtual = fluxo

  window.location.href = montarUrlAutorizacao(state, challenge, fluxo)
}

/** Troca o code de autorização por tokens via /auth/callback (grava cookie no backend). */
export async function handleCallback(code: string, state: string): Promise<AuthSession> {
  const esperado = sessionStorage.getItem("sigi_pkce_state")
  const codeVerifier = sessionStorage.getItem("sigi_pkce_verifier")
  const fluxoSalvo = sessionStorage.getItem("sigi_pkce_fluxo")
  const fluxo: FluxoLogin = fluxoSalvo === "govbr" ? "govbr" : "interno"
  sessionStorage.removeItem("sigi_pkce_verifier")
  sessionStorage.removeItem("sigi_pkce_state")
  sessionStorage.removeItem("sigi_pkce_fluxo")

  if (!esperado || esperado !== state) {
    throw new Error("State inválido (possível CSRF).")
  }
  if (!codeVerifier) {
    throw new Error("PKCE: code_verifier ausente.")
  }

  const config = configuracao(fluxo)
  const { data } = await authClient.post<AuthSession>(
    `${AUTH_API_URL}/auth/callback`,
    { code, redirectUri: config.redirectUri, codeVerifier, fluxo },
  )
  fluxoAtual = fluxo
  setAccessToken(data)
  return data
}

/**
 * Renova o access token via /auth/refresh (o backend lê o cookie HttpOnly).
 * Single-flight: chamadas concorrentes compartilham a mesma requisição —
 * essencial porque o Keycloak rotaciona o refresh token (cookie) a cada uso.
 */
export function renovarToken(fluxo: FluxoLogin = fluxoAtual): Promise<boolean> {
  const existente = refreshPromises.get(fluxo)
  if (existente) return existente

  const promessa = executarRefresh(fluxo).finally(() => {
    refreshPromises.delete(fluxo)
  })
  refreshPromises.set(fluxo, promessa)
  return promessa
}

async function executarRefresh(fluxo: FluxoLogin): Promise<boolean> {
  try {
    const { data } = await authClient.post<AuthSession>(
      `${AUTH_API_URL}/auth/refresh`,
      { fluxo },
    )
    fluxoAtual = fluxo
    setAccessToken(data)
    return true
  } catch {
    // falhou: não mantém token vencido/rejeitado em memória
    if (fluxoAtual === fluxo) {
      accessToken = null
      accessTokenExpiresAt = 0
    }
    return false
  }
}

/**
 * Monta a URL de logout no Keycloak (RP-initiated logout):
 * revoga a sessão SSO do IdP, e não apenas o refresh token local.
 * Fallback: /login da própria SPA (sem Keycloak configurado).
 */
export function montarUrlLogout(fluxo: FluxoLogin = fluxoAtual): string {
  const config = configuracao(fluxo)
  const base =
    config.logoutUri ||
    (config.url && config.realm
      ? `${config.url}/realms/${config.realm}/protocol/openid-connect/logout`
      : null)
  if (!base) return "/login"
  const params = new URLSearchParams({
    post_logout_redirect_uri: POST_LOGOUT_REDIRECT_URI,
    client_id: config.clientId,
  })
  return `${base}?${params.toString()}`
}

/**
 * URL do console de conta do Keycloak (troca de senha, MFA e dados da conta).
 * A senha é gerenciada pelo SSO, nunca pelo SIGI. Sem Keycloak configurado
 * retorna vazio — a UI oculta o atalho nesse caso.
 */
export function montarUrlConsoleConta(): string {
  if (!AUTH_URL || !REALM) return ""
  return `${AUTH_URL}/realms/${REALM}/account`
}

/**
 * Revoga a sessão no backend (limpa o cookie), limpa o token em memória e
 * redireciona para o encerramento da sessão no Keycloak (RP-initiated logout).
 * Nunca lança: falha de rede não impede o logout local + navegação.
 */
export async function logout(fluxo: FluxoLogin = fluxoAtual): Promise<void> {
  try {
    await authClient.post(`${AUTH_API_URL}/auth/logout`, { fluxo })
  } catch {
    // melhor esforço: segue para encerrar a SSO mesmo se a revogação falhar
  } finally {
    accessToken = null
    accessTokenExpiresAt = 0
    window.location.href = montarUrlLogout(fluxo)
  }
}

/**
 * Busca o perfil do usuário autenticado no serviço de autenticação
 * ({@code /api/v1/me} do sigi-auth). Injeta o access token manualmente,
 * pois esse cliente não passa pelos interceptors do cliente de negócio.
 * Em 401 tenta um único refresh (single-flight) e repete a chamada.
 */
export async function consultarPerfil<T>(): Promise<T | null> {
  try {
    return await getMe<T>()
  } catch (err) {
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      const renovado = await renovarToken()
      if (!renovado) return null
      try {
        return await getMe<T>()
      } catch {
        return null
      }
    }
    return null
  }
}

async function getMe<T>(): Promise<T> {
  const token = getAccessToken()
  const { data } = await axios.get<T>(`${AUTH_API_URL}/api/v1/me`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    timeout: 10_000,
    withCredentials: true,
  })
  return data
}

/**
 * Catálogo de grupos do switcher de grupo ativo ({@code /api/v1/grupos} do
 * sigi-auth): admin vê todos os ativos; não-admin, apenas os próprios.
 * Degradada para lista vazia em falha.
 */
export async function consultarGrupos(): Promise<GrupoInfo[]> {
  try {
    const token = getAccessToken()
    const { data } = await axios.get<GrupoInfo[]>(`${AUTH_API_URL}/api/v1/grupos`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      timeout: 10_000,
      withCredentials: true,
    })
    return data
  } catch {
    return []
  }
}

// ---------------------------------------------------------------------------
// PKCE (RFC 7636)
// ---------------------------------------------------------------------------

function gerarCodeVerifier(): string {
  const bytes = new Uint8Array(64)
  crypto.getRandomValues(bytes)
  return base64Url(bytes)
}

export async function gerarCodeChallenge(verifier: string): Promise<string> {
  const data = new TextEncoder().encode(verifier)
  const digest = await crypto.subtle.digest("SHA-256", data)
  return base64Url(new Uint8Array(digest))
}

function base64Url(bytes: Uint8Array): string {
  let bin = ""
  for (const b of bytes) bin += String.fromCharCode(b)
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")
}
